import { gql } from 'graphql-request';
import networkAdapter from '../adapters/network';
import { pollRepeatedly } from 'api/shared';
import { parseJobStatusForSuccess, parseJobStatusForFailure } from './zoneGeneratorParser';
import { uapiFetcher } from 'util/UAPI';
import { trackError } from 'util/error';
import { AUTOZONES_STATUS, ZONE_GENERATION_MODEL } from 'manual/constants';

export const clusterZonesMutation = ({userId, fieldId, numberOfZones, eventDate}) => {
  const query = `
  mutation {
    clusterZones(input:{
      fieldId:${fieldId},
      numberOfZones:${numberOfZones},
      eventDate:\"${eventDate}\",
      layer:CCI
    }) {
      jobId
    }
  }
  `;
  const options = {
    userId,
    query,
    olVersion: 2
  };
  return networkAdapter({
    options,
    useOL: true
  });
};

export const completeMapLayersMutation = ({userId, fieldId, year}) => {
  const query = `
  mutation{
    calculateCompleteLayers(input:{
      fieldId:${fieldId},
      year:\"${year}\",
      layer:CCI
    }) {
      jobId
    }
  }
  `;
  const options = {
    userId,
    query,
    olVersion: 2
  };
  return networkAdapter({
    options,
    useOL: true
  });
};

export const fetchClusteredZones = ({userId, fieldId, numberOfZones, eventDate}) => {
  const query = `
  query {
    clusteredZones(input:{
      fieldId:${fieldId},
      numberOfZones:${numberOfZones},
      eventDate:\"${eventDate}\",
      layer:CCI
    }) {
      recommendation{
        features{
          type
          properties{
            zoneMetadata{
              median,
              minimum,
              maximum
            }
          }
          geometry{
            type
            coordinates
          }
        }
      }
    }
  }
  `;
  const options = {
    userId,
    query,
    olVersion: 2
  };
  return networkAdapter({
    options,
    useOL: true
  });
};

export const fetchCompleteMapLayers = ({userId, fieldId, year}) => {
  const query = `
  query {
    completeLayers(input:{
      fieldId:${fieldId},
      year:\"${year}\",
      layer:CCI
    }){
      completeLayers{
        eventDate,
        layerUuid
      }
    }
  }
  `;
  const options = {
    userId,
    query,
    olVersion: 2
  };
  return networkAdapter({
    options,
    useOL: true
  });
};

const fetchJobStatus = ({userId, jobId}) => {
  const query = `
  query {
    jobsDomain{
      job(id: \"${jobId}\"){
        status
      }
    }
  }
  `;
  const options = {
    userId,
    query,
    olVersion: 1
  };
  return networkAdapter({
    options,
    useOL: true
  });
};

export const pollJobStatus = ({userId, jobId}) => {
  return pollRepeatedly({
    callToPoll: () => fetchJobStatus({ userId, jobId }),
    pollUntil: result => parseJobStatusForSuccess(result),
    abortIf: result => parseJobStatusForFailure(result)
  });
};

export const fetchAvailableLayers = async ({
  cropIds = [],
  fieldUuid,
  results = { yieldLayers: [], cciLayers: [] },
  after = null
}) => {
  const query = gql`
    query ZoneGenerationAvailableData(
    $targets: [ZoneGenerationTarget!]!
    $cropIds: [ID]
    $seasons: [SeasonName]
    $showOnlyUsableLayers: Boolean
    $years: [Int]
    $after: String
    $first: Int
  ) {
    zoneGenerationAvailableData(
      targets: $targets
      filter: { cropIds: $cropIds, seasons: $seasons, showOnlyUsableLayers: $showOnlyUsableLayers, years: $years }
      after: $after
      first: $first
    ) {
      edges {
        node {
          yieldLayers {
            cropId
            eventDate
            id
            isUsable
            season
            year
            averageYield {
              u
              v
            }
          }
          cciLayers {
            id
            year
            season
            imageryThumbnailUrl
            eventDate
            isUsable
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }`;

  const variables = {
    targets: [{ id: fieldUuid, type: 'FIELD' }],
    seasons: [],
    cropIds,
    showOnlyUsableLayers: false,
    years: [],
    first: 100,
    after
  };

  try {
    const { zoneGenerationAvailableData } = await uapiFetcher(query, variables);
    results.yieldLayers.push(...zoneGenerationAvailableData.edges[0].node.yieldLayers);
    results.cciLayers.push(...zoneGenerationAvailableData.edges[0].node.cciLayers);

    if (zoneGenerationAvailableData.pageInfo.hasNextPage) {
      return fetchAvailableLayers({
        cropIds,
        fieldUuid,
        results,
        after: zoneGenerationAvailableData.pageInfo.endCursor
      });
    }
    return results;
  } catch (error) {
    trackError(new TypeError(
      'Could not fetch "ZoneGenerationAvailableData" query from UAPI'), { error, query, variables }
    );
    throw error;
  }
};

export const fetchGeneratedZones = async ({ fieldUuid, numberOfZones, model, selectedLayersIds }) => {
  const cciLayers = model === ZONE_GENERATION_MODEL.cci ? selectedLayersIds : null;
  const yieldLayers = model === ZONE_GENERATION_MODEL.yield ? selectedLayersIds : null;
  const query = gql`
    mutation GenerateZones($input: GenerateZonesInput!) { 
      generateZones(input: $input) {
        id
        numZones
        status
        features {
          geometry
          type
          properties {
            area {
              v
              u
            }
            isDeadZone
            averageCCI {
              v
              u
            }
            relativeProductivity {
              v
              u
            }
            yieldPerCrop {
              crop {
                id
              }
              averageYield {
                v
                u
              }
            }
          }
        }
      }
  }`;

  const variables = {
    input: {
      numberOfZones: Number(numberOfZones),
      target: {
        id: fieldUuid,
        type: 'FIELD'
      },
      model,
      cciLayers: {
        layers: cciLayers
      },
      yieldLayers: {
        layers: yieldLayers
      }
    }
  };

  try {
    const { generateZones } = await pollRepeatedly({
      callToPoll: () => uapiFetcher(query, variables),
      pollUntil: (data) => data?.generateZones?.status !== AUTOZONES_STATUS.PENDING
    });

    return generateZones;
  } catch (error) {
    trackError(new TypeError(
      'Could not fetch "GenerateZones" mutation from UAPI'), { error, query, variables }
    );
    throw error;
  }
};

export const fetchAvailableLayersProcessingStatus = async (fieldUuid) => {
  const query = gql`
    mutation ProcessZoneGenerationAvailableData($targets: [ZoneGenerationTarget!]!) {
      processZoneGenerationAvailableData(targets: $targets) {
        status
      }
    }
  `;

  const variables = {
    targets: [{ type: 'FIELD', id: fieldUuid }]
  };

  try {
    const { processZoneGenerationAvailableData } = await pollRepeatedly({
      callToPoll: () => uapiFetcher(query, variables),
      pollUntil: (data) => data?.processZoneGenerationAvailableData?.[0].status === AUTOZONES_STATUS.ACTIVE
    });

    return processZoneGenerationAvailableData[0].status;
  } catch (error) {
    trackError(new TypeError(
      'Could not fetch "processZoneGenerationAvailableData" mutation from UAPI'), { error, query, variables }
    );
    throw error;
  }
};

export const fetchDataProcessingAndAvailableLayers = async (fieldUuid, cropIds) => {
  await fetchAvailableLayersProcessingStatus(fieldUuid);
  return fetchAvailableLayers({ fieldUuid, cropIds });
};
